"use client";

import { type Ref, useId } from "react";
import {
  linearScale,
  niceStep,
  tickDecimals,
  ticksBetween,
} from "@/lib/tool-kit/math/plot";
import { CHART_INK } from "@/lib/tool-kit/palette";

/**
 * A small Cartesian plot for mechanics graphs: velocity–time lines, trajectories.
 *
 * Colours and fonts are attributes, not classes, so the same SVG downloads as a PNG that
 * looks like the one on the page. All visible text comes in through props.
 */

export const PLOT_WIDTH = 480;
export const PLOT_HEIGHT = 360;
export const PLOT_MARGIN = { left: 52, right: 18, top: 18, bottom: 44 };
const FONT = "system-ui, -apple-system, Segoe UI, sans-serif";
const UNSAFE_ID = /[^a-zA-Z0-9_-]/g;
const LABEL_SIDE_LIMIT = 130;

export interface PlotPoint {
  x: number;
  y: number;
}

export interface PlotSeries {
  points: readonly PlotPoint[];
  color: string;
  dashed?: boolean;
  /** Shade between the line and the horizontal axis (y = 0). */
  fill?: boolean;
  width?: number;
}

export interface PlotMarker {
  x: number;
  y: number;
  color: string;
  label?: string;
  /** Put the label under the point. */
  below?: boolean;
}

export interface PlotRange {
  min: number;
  max: number;
}

/** The pixel width and height of the plotting area inside the margins. */
export const PLOT_AREA = {
  width: PLOT_WIDTH - PLOT_MARGIN.left - PLOT_MARGIN.right,
  height: PLOT_HEIGHT - PLOT_MARGIN.top - PLOT_MARGIN.bottom,
};

function pathOf(
  points: readonly PlotPoint[],
  x: (v: number) => number,
  y: (v: number) => number
): string {
  return points
    .map(
      (p, index) =>
        `${index === 0 ? "M" : "L"}${x(p.x).toFixed(2)} ${y(p.y).toFixed(2)}`
    )
    .join(" ");
}

function areaOf(
  points: readonly PlotPoint[],
  x: (v: number) => number,
  y: (v: number) => number
): string {
  if (points.length === 0) {
    return "";
  }
  const first = points[0];
  const last = points.at(-1) ?? first;
  return `${pathOf(points, x, y)} L${x(last.x).toFixed(2)} ${y(0).toFixed(2)} L${x(first.x).toFixed(2)} ${y(0).toFixed(2)} Z`;
}

export function PhysicsPlot({
  title,
  xLabel,
  yLabel,
  xRange,
  yRange,
  series,
  markers = [],
  formatTick,
  svgRef,
}: {
  /** The graph's accessible name — what it shows, in a sentence. */
  title: string;
  xLabel: string;
  yLabel: string;
  xRange: PlotRange;
  yRange: PlotRange;
  series: readonly PlotSeries[];
  markers?: readonly PlotMarker[];
  formatTick: (value: number, decimals: number) => string;
  svgRef?: Ref<SVGSVGElement>;
}) {
  const uid = useId().replace(UNSAFE_ID, "");
  const clipId = `pp-clip-${uid}`;
  const titleId = `pp-title-${uid}`;
  const left = PLOT_MARGIN.left;
  const right = PLOT_WIDTH - PLOT_MARGIN.right;
  const top = PLOT_MARGIN.top;
  const bottom = PLOT_HEIGHT - PLOT_MARGIN.bottom;
  const x = linearScale(xRange.min, xRange.max, left, right);
  const y = linearScale(yRange.min, yRange.max, bottom, top);
  const xStep = niceStep(xRange.max - xRange.min, 6);
  const yStep = niceStep(yRange.max - yRange.min, 6);
  const xTicks = ticksBetween(xRange.min, xRange.max, xStep);
  const yTicks = ticksBetween(yRange.min, yRange.max, yStep);
  const zeroY = yRange.min <= 0 && yRange.max >= 0 ? y(0) : null;
  const zeroX = xRange.min <= 0 && xRange.max >= 0 ? x(0) : null;

  return (
    <svg
      aria-labelledby={titleId}
      className="h-auto w-full max-w-xl"
      fontFamily={FONT}
      ref={svgRef}
      role="img"
      viewBox={`0 0 ${PLOT_WIDTH} ${PLOT_HEIGHT}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title id={titleId}>{title}</title>
      <rect
        fill={CHART_INK.background}
        height={PLOT_HEIGHT}
        width={PLOT_WIDTH}
      />
      <defs>
        <clipPath id={clipId}>
          <rect height={bottom - top} width={right - left} x={left} y={top} />
        </clipPath>
      </defs>
      {xTicks.map((tick) => (
        <g key={`x${tick}`}>
          <line
            stroke={CHART_INK.grid}
            x1={x(tick)}
            x2={x(tick)}
            y1={top}
            y2={bottom}
          />
          <text
            fill={CHART_INK.muted}
            fontSize={11}
            textAnchor="middle"
            x={x(tick)}
            y={bottom + 15}
          >
            {formatTick(tick, tickDecimals(xStep))}
          </text>
        </g>
      ))}
      {yTicks.map((tick) => (
        <g key={`y${tick}`}>
          <line
            stroke={CHART_INK.grid}
            x1={left}
            x2={right}
            y1={y(tick)}
            y2={y(tick)}
          />
          <text
            dominantBaseline="middle"
            fill={CHART_INK.muted}
            fontSize={11}
            textAnchor="end"
            x={left - 6}
            y={y(tick)}
          >
            {formatTick(tick, tickDecimals(yStep))}
          </text>
        </g>
      ))}
      <rect
        fill="none"
        height={bottom - top}
        stroke={CHART_INK.axis}
        width={right - left}
        x={left}
        y={top}
      />
      {zeroY === null ? null : (
        <line
          stroke={CHART_INK.text}
          strokeWidth={1.25}
          x1={left}
          x2={right}
          y1={zeroY}
          y2={zeroY}
        />
      )}
      {zeroX === null ? null : (
        <line
          stroke={CHART_INK.text}
          strokeWidth={1.25}
          x1={zeroX}
          x2={zeroX}
          y1={top}
          y2={bottom}
        />
      )}
      <text
        fill={CHART_INK.text}
        fontSize={12}
        textAnchor="middle"
        x={(left + right) / 2}
        y={PLOT_HEIGHT - 6}
      >
        {xLabel}
      </text>
      <text
        fill={CHART_INK.text}
        fontSize={12}
        textAnchor="middle"
        transform={`rotate(-90 13 ${(top + bottom) / 2})`}
        x={13}
        y={(top + bottom) / 2}
      >
        {yLabel}
      </text>
      <g clipPath={`url(#${clipId})`}>
        {series.map((line, index) => (
          // Series are few and fixed in order for each graph.
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed series order
          <g key={index}>
            {line.fill ? (
              <path
                d={areaOf(line.points, x, y)}
                fill={line.color}
                fillOpacity={0.15}
              />
            ) : null}
            <path
              d={pathOf(line.points, x, y)}
              fill="none"
              stroke={line.color}
              strokeDasharray={line.dashed ? "6 4" : undefined}
              strokeLinejoin="round"
              strokeWidth={line.width ?? 2.5}
            />
          </g>
        ))}
        {markers.map((marker, index) => {
          const cx = x(marker.x);
          const cy = y(marker.y);
          const nearRight = cx > PLOT_WIDTH - LABEL_SIDE_LIMIT;
          const underneath = marker.below === true || cy < top + 24;
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed marker order
            <g key={index}>
              <circle
                cx={cx}
                cy={cy}
                fill={marker.color}
                r={4.5}
                stroke={CHART_INK.background}
                strokeWidth={1.5}
              />
              {marker.label ? (
                <text
                  fill={CHART_INK.text}
                  fontSize={12}
                  textAnchor={nearRight ? "end" : "start"}
                  x={nearRight ? cx - 8 : cx + 8}
                  y={underneath ? cy + 18 : cy - 10}
                >
                  {marker.label}
                </text>
              ) : null}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

/** Pad [min, max] by a fraction and round outward to the tick step. */
export function niceRange(min: number, max: number, pad = 0.08): PlotRange {
  const span = max - min || Math.abs(max) || 1;
  const lo = min - span * pad;
  const hi = max + span * pad;
  const step = niceStep(hi - lo, 6);
  return {
    min: Math.floor(lo / step + 1e-9) * step,
    max: Math.ceil(hi / step - 1e-9) * step,
  };
}
